import React from 'react';
import { useTranslation } from 'react-i18next';

import { useThemeMode } from '@/nova/theme/OwsTheme';
import { SPEI_BRAND_DARK, SPEI_BRAND_LIGHT } from '../../spei.tokens';
import { LogoWrap } from './SpeiLogo.styles';

/**
 * The SPEI wordmark, in its brand colours and on no background.
 *
 * The two tones are load-bearing, not decorative: at a single tone the "E" and the stylised
 * "I" fuse into one blob and the mark reads "SPE".
 *
 * Which pair of values is used depends on the studio theme, because the brand navy `#343084`
 * measures **1.76:1** on the dark checkout pane — unreadable. The old white plate behind the
 * mark existed to fix exactly that; with the plate gone, the dark variant carries a lightened
 * navy of the same hue instead (5.04:1). The orange is the true brand value in both. See
 * {@link SPEI_BRAND_LIGHT} / {@link SPEI_BRAND_DARK}.
 *
 * `role="img"` + a label rather than `aria-hidden`: this identifies the payment method, so it
 * carries meaning a screen reader needs.
 */
export type SpeiLogoProps = {
  /**
   * Rendered height in px; the width follows from the viewBox ratio. Defaults to the checkout
   * form's 26. Keep it at or above {@link MIN_LEGIBLE_HEIGHT} — the stylised "I" collapses
   * below that.
   */
  height?: number;
};

/** viewBox is 7874.03 × 2659.55, so the mark is this many times wider than it is tall. */
const ASPECT = 7874.03 / 2659.55;

/** Below roughly this height the two wedges of the "I" stop resolving and it reads as noise. */
const MIN_LEGIBLE_HEIGHT = 16;

export const SpeiLogo = ({ height = 26 }: SpeiLogoProps = {}): React.ReactElement => {
  const { t } = useTranslation();
  // Subscribing to the theme re-renders the mark on a light⇄dark switch, so the navy swaps
  // with it. A CSS variable can't do this job: these are literal brand values, not tokens.
  const brand = useThemeMode() === 'light' ? SPEI_BRAND_LIGHT : SPEI_BRAND_DARK;
  return (
    <LogoWrap>
      {/* Both dimensions are explicit so the wrapper reserves the mark's full width — with
          only a height set it paints outside its own box. */}
      <svg
        viewBox="111.84 48.85 7874.03 2659.55"
        width={Math.round(Math.max(height, MIN_LEGIBLE_HEIGHT) * ASPECT)}
        height={Math.max(height, MIN_LEGIBLE_HEIGHT)}
        clipRule="evenodd"
        fillRule="evenodd"
        role="img"
        aria-label={t('SPEI')}
      >
        <path
          d="m1590.45 853.28c239.8-12.21 467.61-30.53 703.82-41.04-67.9-498.98-308.88-763.39-1037.63-763.39-715.59 0-1026.31 367.85-1067.35 705.82-46.18 380.33 240.11 623.95 697.83 757.04 395.35 115.84 726.67 196.34 736.99 390.5 11.11 208.81-227.43 326.15-345.59 326.15-211.18 0-440.92-233.93-468.41-448.34-229.84 18.24-468.43 19.22-698.27 37.46 0 582.44 473.37 890.92 1101.01 890.92 585.3 0 1160.13-246.13 1160.13-875.57 0-764.21-826.87-763.98-1368.08-952.19-189.25-104.48-90.56-333.08 87.56-353.16 359.61-43.65 421.35 160.93 497.98 325.8zm2253.12-742.54c469.01 0 801.11 258.43 801.11 782.31 0 480.12-330.42 786.61-810.05 786.61h-599.03v919.38c0 63.16 0 63.16-55.78 63.16h-624.98c-78.25 0-78.25 0-78.25-58.77v-2383.54c0-109.15 0-109.15 109.83-109.15zm-547.73 1083.1c-44.31 0-60.24-18.19-60.24-45.69v-493.89c0-36.22 29.56-56.57 56.57-56.57h262.58c226.01 33.35 308.74 155.81 308.74 315.48 0 228.46-203.65 280.67-380.54 280.67h-187.12z"
          fill={brand.navy}
        />
        <path
          d="m4815.26 181.3c0-32 22.97-56.49 48.29-56.49h1992.19c43.96-1.99 48.75 16.09 48.75 33.2v455.26c2.05 21.13-15.34 30.79-30.79 30.79h-1261.47c-26.36 0-35.08 27.02-35.08 56.54v307.39c0 33.15 23.64 63.46 63.46 63.46h1116.16l25.47 25.47v426.87c-12.76 18.31-25.52 36.61-38.28 54.93h-1124.75c-26.02 0-42.07 16.06-42.07 42.07v409.49c0 22.58 26.91 47.72 58.37 47.72h1243.51c16.44 0 25.46 15.65 25.46 25.46v497.34c0 11.7-13.23 24.48-24.48 24.48h-2044.4c-10.84 0-20.34-10.7-20.34-20.34v-2423.63z"
          fill={brand.orange}
        />
        <path
          d="m7985.86 1535.24v1090.04h-709.39c-10.84 0-20.34-10.7-20.34-20.34v-973.8c0-26.08 18.02-41.09 31.48-41.09h203.66c20.41 0 25.57 17.46 25.57 32.87v455.08c0 57.49 48.2 32.72 73.98 0 131.69-180.92 263.37-361.84 395.05-542.76z"
          fill={brand.navy}
        />
        <path
          d="m7225.12 181.3c0-32 22.97-56.49 48.29-56.49h651.17c45.11 0 61.28 27.62 61.28 49.42v930.78l-398.7-557.94c-8.61-12.07-28.31-11.29-28.31 13.7v401.85c0 22.38-11.06 64.84-42.93 64.84h-259.82c-14.16 0-31.01-31.95-31.01-50.23v-795.93z"
          fill={brand.orange}
        />
      </svg>
    </LogoWrap>
  );
};

export default SpeiLogo;
